// apps/client/src/ui/creator/Preview.tsx (M0-T16) — the creator's live 3D doll, slowly turning on a cream disc
import { tuning } from '@whiskerton/content';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import type { AssetService } from '../../assets/service';
import { pageAssets } from '../../assets/shared';
import { IS_DEV } from '../../boot/env';
import { Avatar } from '../../characters/avatar';
import type { DollSpec } from '../../characters/doll';

// The look demo's thumbnail lighting and framing (presentation only, not gameplay tuning).
const SKY = '#FFF6E8', GROUND = '#B89A7A', HEMI = 2.2, SUN = '#FFE2B8', SUN_INTENSITY = 1.6, DISC = '#FFF4E2';
const TURN_SPEED = 0.6;   // radians per second
const MAX_DT = 0.1;       // seconds; a backgrounded tab must not spin the doll in one jump

/** The page's AssetService (title screen, 2026-10-05): the rig the title's villagers loaded, not a second copy. */
const assets = (): AssetService => pageAssets();
let warned = false;
function warnOnce(msg: string, e: unknown): void {
  if (IS_DEV && !warned) { warned = true; console.warn(msg, e); }
}

interface Stage { scene: THREE.Scene; avatar: Avatar | null; waved: boolean; stop(): void }

export function Preview(p: { spec: DollSpec }): React.ReactElement | null {
  const canvas = useRef<HTMLCanvasElement>(null);
  const stage = useRef<Stage | null>(null);
  const [failed, setFailed] = useState(false);   // no WebGL, or no rig because assets aren't built: render nothing

  // Renderer, scene and frame loop: built once per mount, disposed on unmount (or when the doll can't load).
  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    let renderer: THREE.WebGLRenderer;
    try { renderer = new THREE.WebGLRenderer({ canvas: el, alpha: true, antialias: true }); }
    catch (e) { warnOnce('creator preview: no WebGL', e); setFailed(true); return; }
    renderer.toneMapping = THREE.NeutralToneMapping;
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    const scene = new THREE.Scene();
    const height = tuning.move.residentHeight;
    const camera = new THREE.PerspectiveCamera(30, 3 / 4, 0.1, 50);
    camera.position.set(0, height * 0.75, height * 3.6);
    camera.lookAt(0, height * 0.5, 0);
    const sun = new THREE.DirectionalLight(SUN, SUN_INTENSITY);
    sun.position.set(2, 4, 3);
    scene.add(new THREE.HemisphereLight(SKY, GROUND, HEMI), sun);
    const discGeo = new THREE.CircleGeometry(height * 0.7, 48).rotateX(-Math.PI / 2);
    const discMat = new THREE.MeshStandardMaterial({ color: DISC, roughness: 0.9, metalness: 0 });
    scene.add(new THREE.Mesh(discGeo, discMat));

    let w = 0, h = 0, last = performance.now(), raf = 0, stopped = false;
    const s: Stage = {
      scene, avatar: null, waved: false,
      stop() {
        if (stopped) return;
        stopped = true;
        cancelAnimationFrame(raf);
        s.avatar?.dispose();
        s.avatar = null;
        discGeo.dispose();
        discMat.dispose();
        renderer.dispose();
        if (stage.current === s) stage.current = null;
      },
    };
    stage.current = s;
    const frame = (now: number) => {
      const dt = Math.min(MAX_DT, (now - last) / 1000);
      last = now;
      const cw = el.clientWidth, ch = el.clientHeight;
      if (cw > 0 && ch > 0 && (cw !== w || ch !== h)) {
        w = cw; h = ch;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      }
      if (s.avatar) { s.avatar.root.rotation.y += dt * TURN_SPEED; s.avatar.update(dt); }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => s.stop();
  }, []);

  // Rebuild the doll when the spec changes: each rig() call is a fresh clone, so the old doll is disposed rather than re-dressed.
  useEffect(() => {
    const s = stage.current;
    if (!s) return;
    let cancelled = false;
    assets().rig().then((rig) => {
      if (cancelled || stage.current !== s) return;
      const yaw = s.avatar?.root.rotation.y ?? 0;
      s.avatar?.dispose();
      const avatar = Avatar.create(rig, p.spec, tuning.move.residentHeight);
      avatar.root.rotation.y = yaw;
      s.scene.add(avatar.root);
      s.avatar = avatar;
      if (!s.waved) { s.waved = true; avatar.play('wave'); }
    }).catch((e: unknown) => {
      warnOnce('creator preview: rig unavailable (run pnpm assets)', e);
      if (cancelled) return;
      s.stop();
      setFailed(true);
    });
    return () => { cancelled = true; };
  }, [p.spec]);

  if (failed) return null;
  return <canvas ref={canvas} className="creator-canvas" aria-hidden="true" />;
}
